import { useEffect, useState } from "react";
import { Button, Group, NumberInput, Select, Stack, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useCostCodes } from "@/hooks/useCostCodes";
import type { BudgetLine } from "@/hooks/useBudget";

export interface BudgetLineFormData {
  costCodeId: string;
  description: string;
  quantity: number;
  unit: string;
  unitCost: number;
  total: number;
}

interface BudgetLineFormProps {
  onSubmit: (data: BudgetLineFormData) => void;
  onCancel?: () => void;
  initialData?: BudgetLine;
  loading?: boolean;
  submitText?: string;
  projectId?: string;
}

export function BudgetLineForm({
  onSubmit,
  onCancel,
  initialData,
  loading = false,
  submitText = "Save",
}: BudgetLineFormProps) {
  const { data: costCodesData } = useCostCodes();
  const [totalMode, setTotalMode] = useState<"calculated" | "manual">("calculated");

  const form = useForm<BudgetLineFormData>({
    initialValues: {
      costCodeId: initialData?.costCodeId || "",
      description: initialData?.description || "",
      quantity: initialData?.quantity || 1,
      unit: initialData?.unit || "",
      unitCost: initialData?.unitCost || 0,
      total: initialData?.total || 0,
    },
    validate: {
      costCodeId: (value) => (!value ? "Cost code is required" : null),
      description: (value) => (!value.trim() ? "Description is required" : null),
      quantity: (value) => (value <= 0 ? "Quantity must be greater than 0" : null),
      unit: (value) => (!value.trim() ? "Unit is required" : null),
      unitCost: (value) => (value < 0 ? "Unit cost cannot be negative" : null),
      total: (value) => (value <= 0 ? "Total must be greater than 0" : null),
    },
  });

  // Auto-calculate total when quantity or unitCost changes
  useEffect(() => {
    if (totalMode === "calculated") {
      const calculatedTotal = form.values.quantity * form.values.unitCost;
      form.setFieldValue("total", calculatedTotal);
    }
  }, [form.values.quantity, form.values.unitCost, totalMode]);

  // When total is manually edited, recalculate unit cost
  const handleTotalChange = (value: number | string) => {
    const total = typeof value === "string" ? parseFloat(value) || 0 : value;
    form.setFieldValue("total", total);

    // Recalculate unitCost based on total and quantity
    if (form.values.quantity > 0) {
      const newUnitCost = total / form.values.quantity;
      form.setFieldValue("unitCost", newUnitCost);
    }

    setTotalMode("manual");
  };

  const handleQuantityOrUnitCostFocus = () => {
    setTotalMode("calculated");
  };

  const handleSubmit = form.onSubmit((values) => {
    onSubmit(values);
  });

  // Prepare cost code options grouped by division (Mantine v7 format)
  const costCodes = costCodesData?.costCodes ?? [];
  const groupedByDivision = costCodes.reduce((acc, cc) => {
    const division = cc.division || "Other";
    if (!acc[division]) {
      acc[division] = [];
    }
    acc[division].push({ value: cc.id, label: `${cc.code} - ${cc.name}` });
    return acc;
  }, {} as Record<string, { value: string; label: string }[]>);

  const costCodeOptions = Object.entries(groupedByDivision)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([group, items]) => ({ group, items }));

  return (
    <form onSubmit={handleSubmit}>
      <Stack gap="md">
        <Select
          label="Cost Code"
          placeholder="Select cost code"
          data={costCodeOptions}
          searchable
          required
          {...form.getInputProps("costCodeId")}
        />

        <TextInput
          label="Description"
          placeholder="Enter description"
          required
          {...form.getInputProps("description")}
        />

        <Group grow>
          <NumberInput
            label="Quantity"
            placeholder="0"
            min={0}
            decimalScale={2}
            thousandSeparator=","
            required
            {...form.getInputProps("quantity")}
            onFocus={handleQuantityOrUnitCostFocus}
          />

          <TextInput
            label="Unit"
            placeholder="e.g., SF, LF, EA"
            required
            {...form.getInputProps("unit")}
          />
        </Group>

        <Group grow>
          <NumberInput
            label="Unit Cost"
            placeholder="0.00"
            prefix="$"
            min={0}
            decimalScale={2}
            thousandSeparator=","
            required
            {...form.getInputProps("unitCost")}
            onFocus={handleQuantityOrUnitCostFocus}
          />

          <NumberInput
            label="Total"
            placeholder="0.00"
            prefix="$"
            min={0}
            decimalScale={2}
            thousandSeparator=","
            required
            value={form.values.total}
            onChange={handleTotalChange}
            styles={{
              input: {
                fontWeight: 600,
              },
            }}
          />
        </Group>

        <Group justify="flex-end" mt="md">
          {onCancel && (
            <Button variant="subtle" onClick={onCancel} disabled={loading}>
              Cancel
            </Button>
          )}
          <Button type="submit" loading={loading}>
            {submitText}
          </Button>
        </Group>
      </Stack>
    </form>
  );
}
